import React, { useMemo } from "react";
import {
  useAddRawMaterialMutation,
  useDeleteRawMaterialMutation,
  useGetRawMaterialsQuery,
  useUpdateRawMaterialMutation,
} from "../../features/rawMaterials/rawMaterialApi";
import { useLazyPlantOptions } from "../../features/plants/usePlantOptions";
import MasterCrudPage from "../masters/MasterCrudPage";
import { withAuditFilters } from "../masters/filterMetadata";
import { getPlantLabel, renderPlantCell } from "../masters/masterMetadata";

const rawMaterialFilterFields = withAuditFilters([
  { name: "raw_material_id", label: "Raw Material ID", optionSource: "rawMaterials" },
  { name: "raw_material_name", label: "Raw Material Name" },
  { name: "raw_material_code", label: "Raw Material Code" },
  { name: "qr_code", label: "QR Code" },
  { name: "is_processed", label: "Processed", type: "select", options: [
    { value: "false", label: "No" }, { value: "true", label: "Yes" },
  ] },
]);

const RawMaterial = () => {
  const {
    options: plantOptions,
    isFetching: isLoadingPlants,
    loadOptions: loadPlantOptions,
  } = useLazyPlantOptions();

  const fields = useMemo(
    () => [
      {
        name: "plant_id",
        label: "Plant",
        type: "select",
        options: plantOptions,
        placeholder: isLoadingPlants ? "Loading plants..." : "Select plant",
        disabled: isLoadingPlants && plantOptions.length === 0,
        disabledOnEdit: true,
        required: true,
        fullWidth: true,
      },
      {
        name: "raw_material_name",
        label: "Raw Material Name",
        placeholder: "Enter raw material name",
        required: true,
      },
      {
        name: "raw_material_code",
        label: "Raw Material Code",
        placeholder: "Enter raw material code",
        required: true,
      },
      {
        name: "qr_code",
        label: "QR Code",
        placeholder: "Enter QR code",
      },
      { name: "is_processed", label: "Processed", type: "checkbox" },
    ],
    [isLoadingPlants, plantOptions]
  );

  const columns = useMemo(
    () => (metaData) => [
      {
        name: "Raw Material ID",
        selector: (row) => row?.raw_material_id || "-",
        sortable: true,
      },
      {
        name: "Raw Material Name",
        selector: (row) => row?.raw_material_name || "-",
        sortable: true,
        grow: 2,
      },
      {
        name: "Raw Material Code",
        selector: (row) => row?.raw_material_code || "-",
        sortable: true,
      },
      {
        name: "QR Code",
        selector: (row) => row?.qr_code || "-",
        sortable: true,
      },
      {
        name: "Processed",
        selector: (row) => row?.is_processed ? "Yes" : "No",
        sortable: true,
      },
      {
        name: "Plant",
        selector: (row) => getPlantLabel(row, metaData),
        cell: (row) => renderPlantCell(row, metaData),
        sortable: true,
        grow: 2,
      },
    ],
    []
  );

  return (
    <MasterCrudPage
      entityName="Raw Material"
      pageTitle="Raw Materials"
      idField="raw_material_id"
      displayField="raw_material_name"
      fields={fields}
      columns={columns}
      searchFields={[
        "raw_material_id",
        "raw_material_name",
        "raw_material_code",
        "qr_code",
        "is_processed",
        "plant_id",
        (row, metaData) => getPlantLabel(row, metaData),
      ]}
      searchPlaceholder="Search raw materials"
      useListQuery={useGetRawMaterialsQuery}
      useCreateMutation={useAddRawMaterialMutation}
      useUpdateMutation={useUpdateRawMaterialMutation}
      useDeleteMutation={useDeleteRawMaterialMutation}
      filterFields={rawMaterialFilterFields}
      transformPayload={(payload, editData) => {
        const updatePayload = { ...payload, qr_code: payload.qr_code || null };
        if (!editData) return updatePayload;
        delete updatePayload.plant_id;
        return updatePayload;
      }}
      onOpenForm={loadPlantOptions}
      noDataLabel="No raw materials found"
    />
  );
};

export default RawMaterial;
